import ContentWrapper from '@oberoninternal/travelbase-website/dist/components/ContentWrapper';
import styled from 'styled-components';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import React, { FC, useState } from 'react';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Page from '@oberoninternal/travelbase-website/dist/components/Page';
import { NEWSLETTER_FOOTER } from '@oberoninternal/travelbase-website/dist/constants/registerSource';
import { Flex, Box } from '@rebass/grid';
import { Form, Formik } from 'formik';
import { TextInputField } from '@oberoninternal/travelbase-ds/components/form/TextInput';
import * as Yup from 'yup';
import subscribeToNewsletter from '../utils/subscribeToNewsletter';
import TertiaryButton from './TertiaryButton';

const messages = defineMessages({
    emailPlaceholder: {
        defaultMessage: 'Vul hier je e-mailadres in',
    },
});

const validationSchema = Yup.object().shape({
    newsletterEmail: Yup.string().email().required(),
});

const NewsletterSignup = () => {
    const { formatMessage, locale } = useIntl();
    const [subscribed, setSubscribed] = useState(false);

    return (
        <NewsletterSignupWrapper>
            <StyledContentWrapper>
                <Container py={[7, 8]}>
                    <Formik
                        validationSchema={validationSchema}
                        onSubmit={values => {
                            subscribeToNewsletter(values.newsletterEmail, locale, NEWSLETTER_FOOTER);
                            setSubscribed(true);
                        }}
                        initialValues={{ newsletterEmail: '' }}
                    >
                        {({ isValid }) =>
                            !subscribed ? (
                                <>
                                    <Box mb={[0, 3]}>
                                        <Title variant="large">
                                            <FormattedMessage defaultMessage="Meld je aan" />
                                        </Title>
                                    </Box>

                                    <Box mb={[6, 5]}>
                                        <StyledBody>
                                            <FormattedMessage defaultMessage="Voor een wekelijkse dosis inspiratie, voorpret én natuurlijk de beste acties en aanbiedingen, meld je je aan voor onze nieuwsbrief." />
                                        </StyledBody>
                                    </Box>

                                    <Form autoComplete="off">
                                        <StyledFlex mx={[0, -3, -4]}>
                                            <Box width={[1 / 1, 1 / 1, 2 / 4]} mb={[4, 3]} px={[0, 3, 4]}>
                                                <StyledTextInputField
                                                    type="email"
                                                    name="newsletterEmail"
                                                    placeholder={formatMessage(messages.emailPlaceholder)}
                                                    hideErrorMessage
                                                />
                                            </Box>
                                            <Box width={[1 / 1, 1 / 1, 1 / 4]} mb={[4, 3]} px={[0, 3, 4]}>
                                                <StyledButton size="large" disabled={!isValid} type="submit">
                                                    <FormattedMessage defaultMessage="Inschrijven" />
                                                </StyledButton>
                                            </Box>
                                        </StyledFlex>
                                    </Form>
                                </>
                            ) : (
                                <>
                                    <Box mb={[4, 3]}>
                                        <Title variant="large">
                                            <FormattedMessage defaultMessage="Nog één laatste stap" />
                                        </Title>
                                    </Box>

                                    <Body>
                                        <FormattedMessage defaultMessage="Er is een activatielink naar je e-mailadres gestuurd. Open deze e-mail om te bevestigen." />
                                    </Body>
                                </>
                            )
                        }
                    </Formik>
                </Container>
            </StyledContentWrapper>
        </NewsletterSignupWrapper>
    );
};

const StyledFlex = styled(Flex)`
    flex-direction: column;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        flex-direction: row;
    }
`;
const StyledButton = styled(TertiaryButton)`
    width: 100%;
    height: 4.8rem;
    border-radius: 4.8rem;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        max-width: 20rem;
    }
`;
const StyledTextInputField = styled(TextInputField)`
    height: 4.8rem;
    border-radius: 4.8rem;
`;

const Container = styled(Box)`
    display: flex;
    flex-direction: column;
    color: ${({ theme }) => theme.colors.neutral[0]};
    margin-right: auto;
    margin-left: auto;
    justify-content: center;
    position: relative;
    z-index: 2;

    h1 {
        color: inherit;
        font-weight: 800;
    }
`;

const StyledContentWrapper = styled(ContentWrapper as FC<React.PropsWithChildren<unknown>>)`
    position: relative;
    overflow: hidden;

    :before {
        position: absolute;
        content: '';
        display: block;
        background-image: url('/static/img/vvvtexel-land-alt.svg');
        background-size: cover;
        background-repeat: no-repeat;
        background-position: top right;
        width: 64%;
        top: 32%;
        bottom: 0;
        right: -3.2rem;
        @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
            background-position: top center;
            width: 24%;
            min-width: 37.8rem;
            height: 100%;
            top: 3.2rem;
            bottom: 0;
            right: var(--wrapperPadding);
        }
        @media screen and (min-width: ${({ theme }) => theme.mediaQueries.l}) and (max-width: ${({ theme }) =>
                theme.mediaQueries.xxxl}) {
            right: var(--paddingRight);
        }
    }
`;

const StyledBody = styled(Body)`
    max-width: 32rem;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        max-width: none;
    }
`;

const NewsletterSignupWrapper = styled(Page as FC<React.PropsWithChildren<unknown>>)`
    background: ${({ theme }) => theme.colors.primary[40]};
`;

export default NewsletterSignup;
